import { useEffect, useRef, useState } from "react";
import { FiVolumeX } from "react-icons/fi";
import type { EventConfig, MediaManifest } from "@firsttoast/shared";

interface Props {
  event: EventConfig;
  guestName: string;
  onDone: () => void;
}

const SLIDE_MS = 4000;

function formatDate(iso?: string): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Multimedia intro with graceful degradation:
 *  - With a video (photos + music in one file), it plays as an editorial split:
 *    the portrait video as a full-height panel beside the event details. It
 *    tries to start *with sound*; browsers block autoplay-with-sound until the
 *    user interacts, so if refused we play muted and show "tap for sound".
 *  - With no video but audio, it runs an ambient image slideshow with music.
 *  - The static fallback is a scroll of the photos and still offers the music
 *    (played from the same video file), for when playback is blocked entirely.
 */
export function MediaIntro({ event, guestName, onDone }: Props) {
  const media = event.media;
  const hasVideo = !!media.video;
  const [mode, setMode] = useState<"video" | "slideshow" | "static">(
    hasVideo ? "video" : "slideshow"
  );

  if (mode === "static") {
    return <StaticIntro media={media} title={event.title} onDone={onDone} />;
  }
  if (mode === "video" && media.video) {
    return (
      <EditorialVideoIntro
        video={media.video}
        event={event}
        guestName={guestName}
        onDone={onDone}
        onFallback={() => setMode("static")}
      />
    );
  }
  return (
    <Slideshow
      media={media}
      title={event.title}
      onDone={onDone}
      onFallback={() => setMode("static")}
    />
  );
}

// ---- Shared video panel (used by EditorialVideoIntro and MediaCard) -------

function VideoPanel({
  video,
  className,
}: {
  video: NonNullable<MediaManifest["video"]>;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = false;
    el.play()
      .then(() => {
        setStarted(true);
        setMuted(false);
      })
      .catch(() => {
        el.muted = true;
        el.play()
          .then(() => {
            setStarted(true);
            setMuted(true);
          })
          .catch(() => setStarted(false));
      });
  }, []);

  const enableSound = () => {
    const el = ref.current;
    if (!el) return;
    el.muted = false;
    setMuted(false);
    // Only call play() if the video isn't already running — calling it on an
    // already-playing video can cause a restart or stutter on some browsers.
    if (el.paused || el.ended) {
      if (el.ended) el.currentTime = 0;
      el.play().then(() => setStarted(true)).catch(() => undefined);
    }
  };

  return (
    <div
      className={`relative flex w-full items-center justify-center overflow-hidden bg-black ${className ?? ""}`}
    >
      {video.poster && (
        <div
          aria-hidden
          className="animate-ambient absolute inset-0 bg-cover bg-center blur-2xl brightness-[0.45]"
          style={{ backgroundImage: `url(${video.poster})` }}
        />
      )}

      <video
        ref={ref}
        className="relative z-0 h-full w-full object-contain p-3 drop-shadow-[0_18px_45px_rgba(0,0,0,0.55)] lg:p-0"
        poster={video.poster}
        playsInline
        onEnded={() => undefined}
      >
        {video.sources.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </video>

      {/* Play button — full overlay, only while video hasn't started yet */}
      {!started && (
        <button
          className="absolute inset-0 z-10 grid place-items-center bg-black/20"
          onClick={enableSound}
          aria-label="Play"
        >
          <span className="ml-2 h-0 w-0 border-y-[26px] border-l-[44px] border-y-transparent border-l-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]" />
        </button>
      )}

      {/* Sound pill — at ~3/4 down the panel, only while playing muted */}
      {started && muted && (
        <button
          className="absolute bottom-1/4 left-1/2 z-10 -translate-x-1/2 flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-sm font-medium text-white transition hover:bg-black/80 active:scale-95"
          onClick={enableSound}
          aria-label="Tap for sound"
        >
          <FiVolumeX className="h-4 w-4 shrink-0" />
          Tap for sound
        </button>
      )}
    </div>
  );
}

/**
 * Standalone media panel for embedding alongside other content (e.g. the
 * returning-guest split layout). Shows the video if available, otherwise the
 * first photo. Fills its parent container.
 */
export function MediaCard({ event }: { event: EventConfig }) {
  const media = event.media;

  if (media.video) {
    return <VideoPanel video={media.video} className="h-full" />;
  }

  const img = media.images[0];
  if (!img) return null;
  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <img src={img.src} alt={img.alt ?? ""} className="h-full w-full object-contain" />
    </div>
  );
}

// ---- Editorial split: video panel + details panel -----------------------

function EditorialVideoIntro({
  video,
  event,
  guestName,
  onDone,
  onFallback,
}: {
  video: NonNullable<MediaManifest["video"]>;
  event: EventConfig;
  guestName: string;
  onDone: () => void;
  onFallback: () => void;
}) {
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = false;
    el.play()
      .then(() => { setStarted(true); setMuted(false); })
      .catch(() => {
        el.muted = true;
        el.play()
          .then(() => { setStarted(true); setMuted(true); })
          .catch(() => setStarted(false));
      });
  }, []);

  const enableSound = () => {
    const el = ref.current;
    if (!el) return;
    el.muted = false;
    setMuted(false);
    if (el.paused || el.ended) {
      if (el.ended) el.currentTime = 0;
      el.play().then(() => setStarted(true)).catch(() => undefined);
    }
  };

  const dateLabel = formatDate(event.date);

  return (
    <section className="flex min-h-dvh flex-col lg:h-dvh lg:flex-row">
      {/* Video panel: portrait media shown whole over an ambient wash.
          On desktop it sits on the right (order-2); on mobile it stays on top. */}
      <div className="relative flex h-[68vh] w-full items-center justify-center overflow-hidden bg-black lg:order-2 lg:h-full lg:w-1/2">
        {video.poster && (
          <div
            aria-hidden
            className="animate-ambient absolute inset-0 bg-cover bg-center blur-2xl brightness-[0.45]"
            style={{ backgroundImage: `url(${video.poster})` }}
          />
        )}

        {/* object-contain bounds the video to the panel, so it never crops;
            capped to ~82vh on desktop so it sits smaller with room around it. */}
        <video
          ref={ref}
          className="relative z-0 h-full w-full object-contain p-3 drop-shadow-[0_18px_45px_rgba(0,0,0,0.55)] lg:h-[82vh] lg:p-0"
          poster={video.poster}
          playsInline
          onEnded={() => undefined}
          onError={onFallback}
        >
          {video.sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>

        {/* Play button — full overlay, only while video hasn't started yet */}
        {!started && (
          <button
            className="absolute inset-0 z-10 grid place-items-center bg-black/20"
            onClick={enableSound}
            aria-label="Play"
          >
            <span className="ml-2 h-0 w-0 border-y-[26px] border-l-[44px] border-y-transparent border-l-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]" />
          </button>
        )}

        {/* Sound pill — at ~3/4 down the panel, only while playing muted */}
        {started && muted && (
          <button
            className="absolute bottom-1/4 left-1/2 z-10 -translate-x-1/2 flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-sm font-medium text-white transition hover:bg-black/80 active:scale-95"
            onClick={enableSound}
            aria-label="Tap for sound"
          >
            <FiVolumeX className="h-4 w-4 shrink-0" />
            Tap for sound
          </button>
        )}
      </div>

      {/* Details panel — desktop left (order-1), mobile below the video. */}
      <div className="flex w-full flex-col items-center justify-center gap-3 bg-cream px-6 py-12 text-center lg:order-1 lg:w-1/2 lg:px-14">
        <p className="text-xs uppercase tracking-[0.35em] text-accent">You're invited</p>
        <h1 className="font-script text-6xl leading-tight sm:text-7xl">{event.title}</h1>
        {dateLabel && <p className="text-lg text-muted">{dateLabel}</p>}
        {event.location && <p className="text-lg font-semibold">{event.location}</p>}
        {event.address && <p className="text-muted">{event.address}</p>}

        <div className="my-3 h-px w-16 bg-accent/40" />

        <p className="text-lg">
          Hello, <span className="font-script text-3xl text-accent">{guestName}</span>
        </p>
        <button className="btn-primary mt-2" onClick={onDone}>
          Reserve your spot →
        </button>

        {started && muted && (
          <button className="btn-ghost mt-1 flex items-center gap-2" onClick={enableSound}>
            <FiVolumeX className="h-4 w-4 shrink-0" />
            Tap for sound
          </button>
        )}
        <button
          className="mt-2 text-sm text-muted underline-offset-4 transition hover:text-ink hover:underline"
          onClick={onFallback}
        >
          Prefer a simple version?
        </button>
      </div>
    </section>
  );
}

// Corner controls for the full-bleed slideshow fallback.
function IntroCorners({
  onSkip,
  onFallback,
}: {
  onSkip: () => void;
  onFallback: () => void;
}) {
  return (
    <>
      <button className="btn-scrim absolute right-4 top-4 z-20" onClick={onSkip}>
        Skip intro →
      </button>
      <button
        className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 text-sm
          text-white/70 underline-offset-4 transition hover:text-white hover:underline"
        onClick={onFallback}
      >
        Trouble viewing? Read version
      </button>
    </>
  );
}

// ---- Image slideshow (no-video configs) ---------------------------------

function Slideshow({
  media,
  title,
  onDone,
  onFallback,
}: {
  media: MediaManifest;
  title: string;
  onDone: () => void;
  onFallback: () => void;
}) {
  const [index, setIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && media.audio) audio.play().catch(() => (audio.muted = true));
  }, [media.audio]);

  useEffect(() => {
    if (index >= media.images.length - 1) return;
    const t = setTimeout(() => setIndex((i) => i + 1), SLIDE_MS);
    return () => clearTimeout(t);
  }, [index, media.images.length]);

  const finish = () => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    onDone();
  };

  return (
    <section className="relative h-dvh w-full overflow-hidden bg-black">
      {media.audio && (
        <audio ref={audioRef} loop preload="auto">
          {media.audio.sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </audio>
      )}

      <div
        aria-hidden
        className="animate-ambient absolute inset-0 bg-cover bg-center blur-2xl brightness-[0.45]"
        style={{ backgroundImage: `url(${media.images[index]?.src})` }}
      />

      {media.images.map((img, i) => (
        <img
          key={img.src}
          src={img.src}
          alt={img.alt ?? ""}
          className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/55" />

      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center px-4 pt-[6vh]">
        <h1 className="font-script text-center text-5xl text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.75)] sm:text-7xl">
          {title}
        </h1>
      </div>

      <IntroCorners onSkip={finish} onFallback={onFallback} />
    </section>
  );
}

// ---- Static scroll fallback (keeps the music) ---------------------------

function StaticIntro({
  media,
  title,
  onDone,
}: {
  media: MediaManifest;
  title: string;
  onDone: () => void;
}) {
  const musicRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);

  const toggleMusic = () => {
    const el = musicRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => undefined);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-4 py-10 text-center sm:px-6">
      <h1 className="font-script text-5xl sm:text-6xl">{title}</h1>

      {media.video && (
        <>
          <video ref={musicRef} src={media.video.sources[0]?.src} className="hidden" />
          <button className="btn-ghost mt-4" onClick={toggleMusic}>
            {playing ? "❚❚ Pause music" : "♪ Play music"}
          </button>
        </>
      )}

      {media.images.map((img, i) => {
        const section = media.sections[i];
        return (
          <div className="mt-10" key={img.src}>
            <img
              src={img.src}
              alt={img.alt ?? section?.title ?? ""}
              loading="lazy"
              className="mx-auto max-h-[85vh] w-auto rounded-2xl object-contain shadow-xl"
            />
            {section?.title && <h2 className="mt-4 text-xl font-semibold">{section.title}</h2>}
            {section?.body && <p className="mt-1 text-muted">{section.body}</p>}
          </div>
        );
      })}

      <button className="btn-primary mt-10" onClick={onDone}>
        Continue to RSVP
      </button>
    </section>
  );
}
